<template>
  <AtomicUiCard padding-class="padding-0" class="registry-card create-registry text-center small-12 medium-6 xlarge-5">
    <div class="grid-x text-center registry-card-header-section">
      <div class="h4 registry-card-header full-width">Create a Registry</div>
      <div v-if="createSubheaderText" class="input-text">
        {{ createSubheaderText }}
      </div>
    </div>
    <div class="grid-x registry-card-body-section" :class="{ 'baby-landing-body-section': isMcomWithBabyLanding }">
      <div class="registry-card-body">
        <div class="d-flex box-card align-middle">
          <div>
            <span :class="`${isMcomWithBabyLanding ? 'review-star-large' : 'registry-box'} mcom-info-icon`"></span>
            <span class="bcom-info-icon">&#x2022;</span>
          </div>
          <div class="input-text dashboard-text text-left">
            {{ isBabyLanding ? BabyLandingPage.CreateValueProp1 : WeddingLandingPage.CreateValueProp1 }}
          </div>
        </div>
        <div v-if="!isBabyLanding || isBcom" class="d-flex box-card align-middle">
          <div>
            <span :class="`${isMcomWithBabyLanding ? 'registry-box' : 'review-star-large'} mcom-info-icon`"></span>
            <span class="bcom-info-icon">&#x2022;</span>
          </div>
          <div class="input-text dashboard-text text-left">
            {{ isBabyLanding ? BabyLandingPage.CreateValueProp2 : WeddingLandingPage.CreateValueProp2 }}
          </div>
        </div>
        <div class="d-flex box-card align-middle">
          <div>
            <span class="wedding-registry heart mcom-info-icon"></span>
            <span class="bcom-info-icon">&#x2022;</span>
          </div>
          <div class="input-text dashboard-text text-left">
            {{ createValuePropThree }}
          </div>
        </div>
      </div>
    </div>
    <div class="grid-x registry-card-footer-section cell margin-top-xs">
      <div class="registry-card-footer-button full-width">
        <AtomicUiButton variant="primary" :expanded="true" class="create-registry-button" @click="onGetStarted">
          {{ isBabyLanding ? BabyLandingPage.CreateCTA : WeddingLandingPage.CreateCTA }}
        </AtomicUiButton>
      </div>
      <div class="registry-card-footer-link full-width grid-x align-center">
        <div v-if="isNotSignedIn" class="grid-x cell align-center text-center align-middle">
          Already have a registry?
          <AtomicUiButton variant="link" class="link-body margin-left-xxs signIn" size="small" @click="onSignInClick">
            {{ isBabyLanding ? BabyLandingPage.signInCTA : WeddingLandingPage.signInCTA }}
          </AtomicUiButton>
        </div>
        <div v-else-if="isBabyLanding && registries.length > 0">
          <span>Need to manage a registry?</span>
          <template v-for="(registry, index) in registries" :key="registry.registryGUID">
            <span v-if="index !== 0" class="registry-separation">|</span>
            <a
              href="javascript:void(0);"
              class="registry-selector padding-left-xxs padding-right-xxs"
              :class="{ 'padding-right-xxs': index !== registries.length - 1 }"
              @click="onManageRegistry(registry)"
            >
              {{ registry.name }}
            </a>
          </template>
        </div>
      </div>
    </div>
  </AtomicUiCard>
</template>

<script setup>
import { fireTag } from '@nuxt-common/util/app/utils/tagManager';
import { Cookie } from '@nuxt-common/util/app/utils/cookie';
import { SET_REGISTRY_ID_STATUS, SET_REGISTRY_STATUS_OCCASION } from '~/types/mutations';
import { UPDATE_DEFAULT_REGISTRY } from '~/types/actions';
import { WeddingLandingPage, BabyLandingPage } from 'config';

import { useEnvPropsStore } from '~/stores/envProps';
import { useBaseStore } from '~/stores/base';
import { useCreateRegistryStore } from '~/stores/createRegistry';

const props = defineProps({
  registryId: {
    type: String,
    required: true,
  },
  registries: {
    type: Array,
    required: false,
    default: () => [],
  },
});

const emits = defineEmits(['toggleDeleteModalOverlay']);

const route = useRoute();
const envPropsStore = useEnvPropsStore();
const baseStore = useBaseStore();
const createRegistryStore = useCreateRegistryStore();
const isMcom = computed(() => envPropsStore.envProps.entryPoint?.toUpperCase() === 'MCOM');
const isBcom = computed(() => envPropsStore.envProps.entryPoint?.toUpperCase() === 'BCOM');
const orgId = computed(() => (isMcom.value ? 12 : 13));
const isBabyLanding = computed(() => !!route.path.includes('/registry/baby'));
const isMcomWithBabyLanding = computed(() => isMcom.value && isBabyLanding.value);
const allowCreateRegistry = computed(() => {
  try {
    const registriesObject = JSON.parse(window.localStorage.getItem('userRegistries'));
    return registriesObject.allowedOccasions.split(',').find((allow) => route.path.includes(allow.trim().toLowerCase()));
  } catch {
    return false;
  }
});
const isNotSignedIn = computed(() => {
  if (typeof window !== 'undefined') {
    return !Number(Cookie.get('SignedIn'));
  }
  return true;
});

const isSignedIn = computed(() => !isNotSignedIn.value);
const createValuePropThree = computed(() => {
  if (isBabyLanding.value) {
    return BabyLandingPage.CreateValueProp3;
  }
  return WeddingLandingPage.CreateValueProp3;
});
const createSubheaderText = computed(() => (isBabyLanding.value ? BabyLandingPage.CreateSubheader : WeddingLandingPage.CreateSubheader));

const onGetStarted = () => {
  if (isMcom.value) {
    fireTag('link', {
      link_name: 'create registry get started',
      custom_link_name: 'create registry get started',
    });
  } else {
    fireTag('link', {
      event_name: 'create registry get started',
      link_name: 'create registry get started',
      custom_link_name: 'create registry get started',
    });
  }

  if (isSignedIn.value) {
    if (props.registryId && !allowCreateRegistry.value) {
      navigateTo('/registry/manage');
      createRegistryStore[SET_REGISTRY_STATUS_OCCASION](isBabyLanding.value ? 'baby' : 'wedding');
      createRegistryStore[SET_REGISTRY_ID_STATUS](true);
      emits('toggleDeleteModalOverlay');
    } else if (route.path.includes('/registry/baby')) {
      navigateTo('/registry/baby/create');
    } else {
      navigateTo('/registry/wedding/create');
    }
  } else {
    if (route.path.includes('/registry/baby')) {
      Cookie.set('FORWARDPAGE_KEY', '/registry/baby/create');
    } else {
      Cookie.set('FORWARDPAGE_KEY', '/registry/wedding/create');
    }
    navigateTo('/account/signin', { external: true });
  }
};

const onSignInClick = () => {
  if (isMcom.value) {
    fireTag('link', {
      link_name: 'create registry sign in',
      custom_link_name: 'create registry sign in',
    });
  } else {
    fireTag('link', {
      event_name: 'create registry sign in',
      link_name: 'create registry sign in',
      custom_link_name: 'create registry sign in',
    });
  }
  Cookie.set('FORWARDPAGE_KEY', '/registry');
  navigateTo('/account/signin', { external: true });
};

const onManageRegistry = async (registry) => {
  await baseStore[UPDATE_DEFAULT_REGISTRY]({
    orgId: orgId.value,
    registryGUID: registry.registryGUID,
  });
  navigateTo('/registry/manage');
};
</script>

<style lang="scss" scoped>
@import '~/assets/scss/components/registrySignInStyles.scss';
@import '~@csgIcons/_registry-box';
@import '~@csgIcons/_review-star-large';
@import '~@core/foundation/scss/foundation-6.7.4/scss/prototype/_spacing.scss';

.registry-separation {
  color: $gray-4-color;
}

.registry-card {
  // We need the !important to override the style of the padding-direction mixin
  @include breakpoint(small down) {
    padding-left: rem-calc(20) !important;
    padding-right: rem-calc(20) !important;
  }
}

.create-registry-button {
  width: 100%;
}
</style>
<style lang="scss" scoped>
.registry-card .baby-landing-body-section {
  @include breakpoint(large up) {
    height: rem-calc(100);
    margin-bottom: rem-calc(46);
  }

  @include breakpoint(medium down) {
    height: rem-calc(114);
    margin-top: rem-calc(46);
  }
}
</style>
<style lang="scss" brand="mcom" scoped>
@import '~@core/csg/src/scss/encodedIcons/mcom/_wedding-registry';

.mcom-info-icon {
  display: inline-block;
}
.bcom-info-icon {
  display: none;
}

.registry-card {
  @include padding-direction('left', map-get($spacer, 'm'));
  @include padding-direction('right', map-get($spacer, 'm'));
  @include padding-direction('top', map-get($spacer, 'm'));
  @include padding-direction('bottom', map-get($spacer, 's'));
}
</style>
<style lang="scss" brand="bcom" scoped>
@import '~@core/csg/src/scss/encodedIcons/bcom/_heart';

.mcom-info-icon {
  display: none;
}
.bcom-info-icon {
  display: inline-block;
}

.registry-card {
  max-width: rem-calc(411);
  opacity: 0.85;
  @include padding-direction('left', map-get($spacer, 's'));
  @include padding-direction('right', map-get($spacer, 's'));
  @include padding-direction('top', map-get($spacer, 's'));
  @include padding-direction('bottom', map-get($spacer, 's'));

  @include breakpoint(small down) {
    width: rem-calc(335);
  }

  .registry-card-body-section {
    .registry-card-body {
      margin-left: 0;
      margin-right: 0;

      .box-card {
        @include padding-direction('left', map-get($spacer, 'xs'));
      }

      .box-card:not(:first-child) {
        margin-top: rem-calc(22);
        @include breakpoint(medium only) {
          margin-top: rem-calc(8);
        }
      }
      .box-card:last-child {
        margin-bottom: rem-calc(12);
      }
    }
    .dashboard-text {
      @include margin-direction('left', map-get($spacer, 'xxs'));
    }
  }
}

.registry-card-header {
  font-weight: 900;
  text-transform: uppercase;
}
</style>
